/**
 * Persisted / WS notification helpers (ex-OneSignal).
 * Audience matrix + live clock/ticket formatting: ./notificationMapping.js
 */
import { API } from "../helpers/API/amplifyApi";
import { publish } from "./socket/dashboardSocket";

function stripHtml(html) {
  if (!html) return "";
  if (typeof document !== "undefined") {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.innerText || "";
  }
  return String(html).replace(/<[^>]+>/g, " ").trim();
}

/**
 * Broadcast in-app notification over WebSocket (replaces OneSignal REST).
 * Use only when there is no POST /notifications — the create Lambda fans out
 * live WS for persisted rows (avoids double toasts).
 */
export const sendNotification = ({
  headings = { en: "" },
  contents = { en: "" },
  url = null,
  data = {},
  key,
  value,
  filterType,
  audience,
  identityId,
  userRole,
  entityType,
  entityId,
}) => {
  const title = headings?.en || "";
  const messageText = contents?.en || "";
  let resolvedAudience = audience || "admins";
  let targetIdentityId = identityId || null;
  let targetRole = userRole || null;

  if (key === "userRole" && value) {
    resolvedAudience = value === "Admin" || value === "Super Admin" ? "admins" : "user";
    targetRole = value;
  } else if (key === "identityId" && value) {
    resolvedAudience = "user";
    targetIdentityId = Array.isArray(value) ? value[0] : value;
  } else if (filterType === "multiple" && Array.isArray(value)) {
    value.forEach((id) => {
      publish({
        request: "notification",
        id,
        body: {
          title,
          message: messageText,
          audience: "user",
          identityId: id,
          url,
          data,
          entityType: entityType || null,
          entityId: entityId || null,
          createdAt: Date.now(),
        },
      });
    });
    return;
  }

  publish({
    request: "notification",
    id: targetIdentityId,
    body: {
      title,
      message: messageText,
      audience: resolvedAudience,
      identityId: targetIdentityId,
      userRole: targetRole,
      url,
      data,
      entityType: entityType || null,
      entityId: entityId || null,
      createdAt: Date.now(),
    },
  });
};

/** @deprecated OneSignal history API — no-op after migration */
export const getNotification = () => Promise.resolve(null);

function adminUserDetails(users) {
  const test = {};
  users
    ?.filter((u) => u.userRole === "Admin" || u.userRole === "Super Admin")
    ?.forEach((el) => {
      test[el.identityId] = {
        notificationMarkedHidden: false,
        notificationMarkedDeleted: false,
        notificationMarkedRed: false,
      };
    });
  return test;
}

function allUserDetails(users) {
  const test = {};
  users?.forEach((el) => {
    test[el.identityId] = {
      notificationMarkedHidden: false,
      notificationMarkedDeleted: false,
      notificationMarkedRed: false,
    };
  });
  return test;
}

/** Persist + server-side WS fan-out via notifications/create */
export const notificationUserToAdmin = async (saveData, users) => {
  const test = adminUserDetails(users);
  await API.post("notifications", "/notifications", {
    body: { ...saveData, userDetails: { ...test } },
  });
};

/** WS only (no DynamoDB row) — e.g. clock-out toast */
export const notificationUserToAdminNoDbPost = async (saveData, _users) => {
  sendNotification({
    headings: { en: saveData.notificationTitle },
    contents: { en: stripHtml(saveData.notificationBody) },
    url: saveData.notificationUrl || "/kryefaqja",
    key: "userRole",
    value: "Admin",
  });
};

export const notificationAdminToEveryone = async (saveData, users) => {
  const test = allUserDetails(users);
  await API.post("notifications", "/notifications", {
    body: {
      ...saveData,
      sentTo: saveData.sentTo || "General",
      isAnnouncement: saveData.isAnnouncement ?? true,
      userDetails: { ...test },
    },
  });
};

export const notificationAdminToSecifikUser = async (saveData, users) => {
  const test = allUserDetails(users);
  await API.post("notifications", "/notifications", {
    body: { ...saveData, userDetails: { ...test } },
  });
};

export const notificationAdminToOnlyOneUser = async (saveData, user) => {
  const test = {};
  test[user?.identityId] = {
    notificationMarkedHidden: false,
    notificationMarkedDeleted: false,
    notificationMarkedRed: false,
  };
  await API.post("notifications", "/notifications", {
    body: { ...saveData, userDetails: { ...test } },
  });
};

export const notificationToAllAdminAndSecifikUser = async (
  saveData,
  users,
  user,
  skipSave = false
) => {
  const test = allUserDetails(users);
  const pushBoth = () => {
    const text = stripHtml(saveData.notificationBody);
    sendNotification({
      headings: { en: saveData.notificationTitle },
      contents: { en: text },
      url: saveData.notificationUrl || "/kryefaqja",
      key: "userRole",
      value: "Admin",
    });
    sendNotification({
      headings: { en: saveData.notificationTitle },
      contents: { en: text },
      url: saveData.notificationUrl || "/kryefaqja",
      key: "identityId",
      value: `${user?.identityId}`,
    });
  };

  // Historical quirk: skipSave=true still posts to DB; server create fans out WS.
  if (skipSave) {
    await API.post("notifications", "/notifications", {
      body: { ...saveData, userDetails: { ...test } },
    });
  } else {
    pushBoth();
  }
};

export const sendNotificationToEmployee = async (saveData, user) => {
  const test = {};
  test[user.identityId] = {
    notificationMarkedHidden: false,
    notificationMarkedDeleted: false,
    notificationMarkedRed: false,
  };
  await API.post("notifications", "/notifications", {
    body: { ...saveData, userDetails: { ...test } },
  });
};

export const notificationToDepartment = async (saveData, users) => {
  const test = allUserDetails(users);
  await API.post("notifications", "/notifications", {
    body: { ...saveData, userDetails: { ...test } },
  });
};

/** WS only — announcements to a department without a notifications row */
export const notificationNoPostToDepartment = async (saveData, users) => {
  users?.forEach((el) => {
    sendNotification({
      headings: { en: saveData?.notificationTitle },
      contents: { en: saveData?.content || stripHtml(saveData?.notificationBody) },
      url: saveData?.notificationUrl || "/kryefaqja",
      key: "identityId",
      value: el?.identityId,
    });
  });
};

/** @deprecated OneSignal tag filter helper — kept for import compatibility */
export const getValueOb = (filterType, key, value) => {
  if (filterType === "multiple") {
    return value?.map((el) => {
      return { field: "tag", key: key, relation: "=", value: el };
    });
  }
  return { field: "tag", key: key, relation: "=", value: value };
};
